import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';

export type AppLanguageCode = 'en' | 'ar' | 'he';

const STORAGE_KEY = 'curentLang';

/**
 * Central place for UI language: localStorage, ngx-translate, html/body layout,
 * and optional route refresh so feature components re-read `curentLang`.
 */
@Injectable({ providedIn: 'root' })
export class LanguageService {
	private readonly current$ = new BehaviorSubject<AppLanguageCode>(this.readStoredOrDefault());

	readonly currentLang$ = this.current$.asObservable();

	constructor(
		@Inject(DOCUMENT) private document: Document,
		private translate: TranslateService,
		private router: Router,
	) {}

	/** First app load (AppComponent) — default missing storage to `ar` to match legacy behavior */
	bootstrap(): void {
		let lang = localStorage.getItem(STORAGE_KEY);
		if (!lang) {
			lang = 'ar';
			localStorage.setItem(STORAGE_KEY, lang);
		}
		this.apply(this.normalize(lang), { refreshRoute: false });
	}

	getCurrentLang(): AppLanguageCode {
		return this.current$.value;
	}

	/**
	 * @param refreshRoute When true, re-navigates current URL so routed components
	 * re-run constructors/ngOnInit and pick up the new language from localStorage.
	 */
	apply(lang: string | null | undefined, options?: { refreshRoute?: boolean }): void {
		const code = this.normalize(lang);
		localStorage.setItem(STORAGE_KEY, code);
		this.translate.use(code);
		this.current$.next(code);
		this.applyDocumentLayout(code);
		if (options?.refreshRoute) {
			this.refreshCurrentRoute();
		}
	}

	private readStoredOrDefault(): AppLanguageCode {
		return this.normalize(localStorage.getItem(STORAGE_KEY));
	}

	private normalize(lang: string | null | undefined): AppLanguageCode {
		if (lang === 'en' || lang === 'ar' || lang === 'he') {
			return lang;
		}
		return 'en';
	}

	private applyDocumentLayout(lang: AppLanguageCode): void {
		const html = this.document.getElementsByTagName('html')[0];
		const appContainer = this.document.getElementById('Appcontainer');

		html.classList.remove('ltr', 'rtl');
		appContainer?.classList.remove('is-rtl');

		if (lang === 'en') {
			html.lang = 'en';
			html.dir = 'ltr';
			html.classList.add('ltr');
		} else if (lang === 'ar') {
			html.lang = 'ar';
			html.dir = 'rtl';
			html.classList.add('rtl');
			appContainer?.classList.add('is-rtl');
		} else {
			html.lang = 'he';
			html.dir = 'rtl';
			html.classList.add('rtl');
			appContainer?.classList.add('is-rtl');
		}

		this.document.body.setAttribute('direction', lang === 'en' ? 'ltr' : 'rtl');
		this.document.documentElement.dir = lang === 'en' ? 'ltr' : 'rtl';
	}

	private refreshCurrentRoute(): void {
		const url = this.router.url;
		// Angular <16: `onSameUrlNavigation` is not valid on navigateByUrl extras; hop via a
		// dummy URL so the current route tree is torn down and recreated (re-reads `curentLang`).
		this.router
			.navigateByUrl('/dashboard', { skipLocationChange: true })
			.then(() => this.router.navigateByUrl(url))
			.catch(() => this.router.navigateByUrl(url));
	}
}
